import { TDataRef } from '@/services/data/data.types.ts'
import { RefObject } from 'react'
import CLineChart from './CLineChart'
import { IParameter, IUnit, IUnitPrefix } from '@/pages/Parameters/parameters.types.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import css from '../lineChart.module.css'
import timeUtils from '@/utils/time.utils.ts'

class LineChartCursor {
  private chart: CLineChart
  private tooltipRef: RefObject<HTMLDivElement>
  private lastX: number
  private active: boolean = false

  private readonly parametersMap: Record<IParameter['id'], IParameter>
  private readonly unitsMap: Record<IUnit['id'], IUnit>
  private readonly unitPrefixesMap: Record<IUnitPrefix['id'], IUnitPrefix>

  constructor(
    ref: RefObject<HTMLDivElement>,
    lineChart: CLineChart,
    parametersMap: Record<IParameter['id'], IParameter>,
    unitsMap: Record<IUnit['id'], IUnit>,
    unitPrefixesMap: Record<IUnitPrefix['id'], IUnitPrefix>
  ) {
    this.chart = lineChart
    this.tooltipRef = ref
    this.parametersMap = parametersMap
    this.unitsMap = unitsMap
    this.unitPrefixesMap = unitPrefixesMap

    lineChart.chart.addMouseMoveListener(this.handleMouseMove)
    lineChart.chart.addMouseMoveLeave(this.handleMouseLeave)
  }

  private handleMouseMove = (ev: MouseEvent) => {
    this.active = true
    this.lastX = ev.clientX - this.chart.chart.boundaries.left

    this.render(this.chart.chart.dataRef.current, this.chart.chart.tsFrom, this.chart.chart.tsTo)
  }

  private handleMouseLeave = (_: MouseEvent) => {
    this.active = false
    this.tooltipRef.current.style.opacity = '0'
  }

  private coordToTs = (x: number, t0: number, t1: number, width: number) => {
    if (x <= this.chart.margins.left) return t0
    if (x >= width + this.chart.margins.left) return t1

    const dt = t1 - t0
    const normalizedX = x - this.chart.margins.left

    return t0 + (normalizedX / width) * dt
  }

  private findClosestValues = (ts: number, data: TDataRef) => {
    const values: { deviceId: IEquipment['id']; parameterId: IParameter['id']; value: number; ts: number }[] = []

    for (const axe of this.chart.config.axes) {
      for (const line of axe.lines) {
        const { deviceId, parameterId } = line
        if (!Array.isArray(data[deviceId])) continue

        let closestTs = 0
        let minDt = Number.MAX_SAFE_INTEGER
        let closestValue = null
        for (const point of data[deviceId]) {
          const parameterValue = point.values.find((v) => v.parameterId === parameterId)
          if (!parameterValue) continue

          const dt = Math.abs(point.ts - ts)
          if (dt < minDt) {
            minDt = dt
            closestTs = point.ts
            closestValue = parameterValue.value
          }
        }

        if (closestValue !== null) {
          values.push({ deviceId: deviceId, value: closestValue, parameterId: parameterId, ts: closestTs })
        }
      }
    }

    return values
  }

  public render = (data: TDataRef, t0, t1) => {
    if (!this.active) {
      this.tooltipRef.current.style.opacity = '0'
      return
    }

    const ts = this.coordToTs(this.lastX, t0, t1, this.chart.innerWidth)
    const closestValues = this.findClosestValues(ts, data)

    let valuesStr = ``
    for (const v of closestValues) {
      const parameter = this.parametersMap[v.parameterId]
      let parameterUnit = ''

      if (parameter.unitId) {
        parameterUnit = this.unitsMap[parameter.unitId].nameRu
      }

      let parameterUnitPrefix = ''
      // id === 1 обозначает единицу без префикса
      if (parameter.unitPrefixId && parameter.unitPrefixId !== 1) {
        parameterUnitPrefix = this.unitPrefixesMap[parameter.unitPrefixId].nameRu
      }

      const str = `${parameter.name}: ${v.value.toFixed(2)}${parameterUnitPrefix}${parameterUnit}`
      valuesStr += str + '\n'
    }

    // Вычислить абсолютные координаты линии, но не позволить ей выйти за границы графика
    const lineLeft = Math.min(
      Math.max(this.chart.margins.left, this.lastX),
      this.chart.chart.boundaries.width - this.chart.margins.right
    )
    // Аналогчино вычислить высоту
    const lineHeight = this.chart.chart.boundaries.height - this.chart.margins.bottom - this.chart.margins.top - 4

    this.tooltipRef.current.style.left = lineLeft + 'px'
    this.tooltipRef.current.style.height = lineHeight + 'px'
    this.tooltipRef.current.style.opacity = '1'

    // Заполнить значения
    const valuesElement = this.tooltipRef.current.querySelector(`.${css.tooltipValues}`)
    const timeElement = this.tooltipRef.current.querySelector(`.${css.tooltipTime}`)

    if (!valuesStr) {
      valuesStr = 'Отсутствуют значения для отображения'
    }
    valuesElement.innerHTML = valuesStr
    timeElement.innerHTML = timeUtils.formatDate(ts, 'HH:mm:ss, dd.MM.yyyy')

    // Спозцицонировать тултип слева или справа, в заивисимости от близости к одной из границ
    const tooltipElement = this.tooltipRef.current.querySelector(`.${css.tooltip}`)
    const tooltipRect = tooltipElement.getBoundingClientRect()

    if (lineLeft + tooltipRect.width >= this.chart.chart.boundaries.width - 10) {
      tooltipElement.classList.remove(css.left)
      tooltipElement.classList.add(css.right)
    } else {
      tooltipElement.classList.remove(css.right)
      tooltipElement.classList.add(css.left)
    }
  }
}

export default LineChartCursor
